import { Ban, CircleCheck, TriangleAlert } from 'lucide-react'
import type { ReactNode } from 'react'

import type { CustomerExposure } from '@/api/types'
import { MoneyDisplay } from '@/components/money-display'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'

/**
 * The cross-branch exposure banner.
 *
 * This is the control that stops a customer in arrears at Kaneshie quietly borrowing at Madina.
 * It is computed over every branch — never filtered to the one the operator is sitting in — and
 * it is deliberately the loudest element on the customer screen when there is something to say.
 *
 * When there is nothing to say it stays, quietly. An absent banner and a clear banner look the
 * same to someone scanning a screen at speed, and only one of them means "checked".
 */

export interface CustomerExposureBannerProps {
  exposure: CustomerExposure
  className?: string
}

export function CustomerExposureBanner({ exposure, className }: CustomerExposureBannerProps) {
  const inArrears = exposure.daysInArrears > 0
  const blocking = exposure.isBlacklisted || inArrears
  const owes = exposure.openLoanCount > 0 || exposure.totalOutstandingMinor > 0

  if (!blocking && !owes) {
    return (
      <div
        className={cn(
          'flex items-center gap-2 rounded-card border border-border-subtle bg-surface px-3 py-2',
          className,
        )}
      >
        <CircleCheck className="size-4 shrink-0 text-success-text" aria-hidden />
        <p className="text-xs text-body">
          No exposure anywhere in the institution — no open loans, nothing in arrears, not blacklisted.
        </p>
      </div>
    )
  }

  return (
    <section
      // Announced rather than merely rendered: the operator may be looking at the keyboard when
      // this appears, and it is the one thing on the screen they must not miss.
      role="alert"
      aria-label="Cross-branch exposure"
      className={cn(
        'overflow-hidden rounded-card border-2',
        blocking ? 'border-danger-fill bg-danger-subtle' : 'border-border-strong bg-brand-fill-subtle',
        className,
      )}
    >
      {/*
        The icon sits beside the headline at every width rather than above it on a narrow one.
        Stacked, it would cost a line of vertical space at the top of the screen where this banner
        is competing for the first thing the operator reads.
      */}
      <div className="flex items-start gap-3 p-3 sm:p-4">
        <span
          className={cn(
            'flex size-9 shrink-0 items-center justify-center rounded-full',
            blocking ? 'bg-danger-fill text-on-danger-fill' : 'bg-brand-fill text-on-brand-fill',
          )}
          aria-hidden
        >
          {exposure.isBlacklisted ? <Ban className="size-5" /> : <TriangleAlert className="size-5" />}
        </span>

        <div className="min-w-0 flex-1 space-y-2">
          <div className="space-y-1">
            <h2
              className={cn(
                'text-base font-semibold',
                blocking ? 'text-danger-text' : 'text-heading',
              )}
            >
              {headline(exposure)}
            </h2>
            <p className="text-sm text-body">
              {blocking
                ? 'This position is held across every branch. Do not disburse, restructure, or open a new facility against it without the approval the policy requires.'
                : 'Open borrowing recorded elsewhere in the institution. Take it into account before adding to it.'}
            </p>
            {exposure.blacklistReason ? (
              <p className="text-sm font-medium text-danger-text">
                Reason: {exposure.blacklistReason}
              </p>
            ) : null}
          </div>

          {/*
            One column below `sm`, three above. A money figure is never wrapped or truncated to
            make a narrow column fit — a partly shown amount is worse than a taller banner.
          */}
          <dl className="grid gap-x-6 gap-y-2.5 sm:grid-cols-3">
            <Figure label="Open loans">
              <span className="numeric text-base font-semibold text-heading tabular-nums">
                {exposure.openLoanCount}
              </span>
            </Figure>

            <Figure label="Total outstanding">
              <MoneyDisplay
                value={exposure.totalOutstandingMinor}
                currency={exposure.currencyCode}
                emphasis="strong"
                className="text-left text-base"
              />
            </Figure>

            <Figure label="Days in arrears">
              <span
                className={cn(
                  'numeric text-base font-semibold tabular-nums',
                  inArrears ? 'text-danger-text' : 'text-heading',
                )}
              >
                {exposure.daysInArrears}
              </span>
            </Figure>
          </dl>

          {exposure.branchesWithExposure.length > 0 ? (
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="text-2xs font-semibold tracking-wide text-muted uppercase">
                Branches
              </span>
              {exposure.branchesWithExposure.map((branch) => (
                <Badge key={branch} tone={blocking ? 'danger' : 'brand'}>
                  {branch}
                </Badge>
              ))}
            </div>
          ) : null}
        </div>
      </div>
    </section>
  )
}

function headline(exposure: CustomerExposure): string {
  if (exposure.isBlacklisted && exposure.daysInArrears > 0) {
    return `Blacklisted, and ${String(exposure.daysInArrears)} days in arrears`
  }

  if (exposure.isBlacklisted) {
    return 'Blacklisted across the institution'
  }

  if (exposure.daysInArrears > 0) {
    const branches = exposure.branchesWithExposure.length
    return branches > 1
      ? `${String(exposure.daysInArrears)} days in arrears, across ${String(branches)} branches`
      : `${String(exposure.daysInArrears)} days in arrears`
  }

  return exposure.openLoanCount === 1 ? 'One open loan elsewhere' : 'Open loans across the institution'
}

function Figure({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <dt className="text-2xs font-semibold tracking-wide text-muted uppercase">{label}</dt>
      <dd className="mt-0.5">{children}</dd>
    </div>
  )
}
